import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  DocumentNode,
  NetworkStatus,
  QueryHookOptions,
  QueryResult,
  useApolloClient,
} from '@apollo/client';

import { getError, getTypeOrNull } from './apollo';

type QueryVariables = {
  filter?: string | null;
  pageSize?: number | null;
  pageToken?: string | null;
  orderBy?: string | null;
};

type QueryOptions = {
  query: DocumentNode;
  typename: string;
  fetchPolicy?: QueryHookOptions['fetchPolicy'];
};

type InitialData<TItem> = {
  items: TItem[] | null | undefined;
  nextPageToken: string | null | undefined;
};

export type InitialListData<TItem> = InitialData<TItem>;

export type StandardListHookOptions<TCustomVars = {}, TItem = any> = {
  filter?: string;
  pageSize?: number;
  orderBy?: string;
  skip?: boolean;
  pollInterval?: number;
  fetchPolicy?: QueryHookOptions['fetchPolicy'];
  initialData?: InitialData<TItem>;
} & TCustomVars;

type ListResponse<TItem> = {
  flags: TItem[];
  nextPageToken: string | null;
};

type StandardListHookResult<TItem, TPath extends string> = {
  [K in TPath]: TItem[] | undefined;
} & {
  hasNextPage: boolean;
  error?: Error;
  isLoading: boolean;
  isEmpty: boolean;
  nextPage: () => Promise<void>;
  isLoadingNextPage: boolean;
  refetch: () => void;
};

// Overload for without subscription
export function withStandardListHook<
  TData extends Record<string, any>,
  TItem,
  TPath extends Extract<keyof TData, string>,
  TCustomVars extends Record<string, any> = {},
>(
  useQuery: (
    options?: QueryHookOptions<TData, QueryVariables & TCustomVars>,
  ) => QueryResult<TData, QueryVariables & TCustomVars>,
  dataPath: TPath,
  queryOptions?: QueryOptions,
): (
  options: StandardListHookOptions<TCustomVars, TItem>,
) => StandardListHookResult<TItem, TPath>;

// Implementation
export function withStandardListHook<
  TData extends Record<string, any>,
  TItem,
  TPath extends Extract<keyof TData, string>,
  TCustomVars extends Record<string, any> = {},
>(
  useQuery: (
    options?: QueryHookOptions<TData, QueryVariables & TCustomVars>,
  ) => QueryResult<TData, QueryVariables & TCustomVars>,
  dataPath: TPath,
  queryOptions?: QueryOptions,
) {
  return (
    options: StandardListHookOptions<TCustomVars, TItem>,
  ): StandardListHookResult<TItem, TPath> => {
    const {
      filter,
      pageSize,
      orderBy,
      skip,
      initialData,
      pollInterval,
      fetchPolicy,
      ...customVars
    } = options;

    const client = useApolloClient();
    const writtenQuery = useRef<boolean>(false);

    useLayoutEffect(() => {
      if (queryOptions?.query && initialData && !writtenQuery.current) {
        const variables = {
          filter: filter ?? null,
          pageSize: pageSize ?? null,
          orderBy: orderBy ?? null,
          ...(customVars as TCustomVars),
        };

        const existing = client.cache.readQuery({
          query: queryOptions.query,
          variables,
        });

        if (existing) {
          // We only write the query if it doesn't exist in the cache.
          return;
        }

        client.cache.writeQuery({
          query: queryOptions.query,
          variables,
          data: {
            [dataPath]: {
              __typename: queryOptions.typename,
              [dataPath]: initialData.items,
              nextPageToken: initialData.nextPageToken,
            },
          },
        });

        // We do not want to write the query again even if initial data
        // or options change.
        writtenQuery.current = true;
      }
    }, [initialData, filter, pageSize, orderBy, customVars, client]);

    const {
      data,
      error: networkError,
      loading,
      fetchMore,
      refetch,
      networkStatus,
    } = useQuery({
      variables: {
        filter: filter ?? null,
        pageSize: pageSize ?? null,
        orderBy: orderBy ?? null,
        ...(customVars as TCustomVars),
      },
      pollInterval,
      skip,
      fetchPolicy:
        fetchPolicy ??
        queryOptions?.fetchPolicy ??
        (initialData ? 'cache-first' : undefined),
      notifyOnNetworkStatusChange: true,
    });

    // Get the nested data using the provided path
    const nestedData = data?.[dataPath];
    const typedData = getTypeOrNull(nestedData) as ListResponse<TItem> | null;
    const appError = getError(nestedData) ?? undefined;

    // @ts-expect-error
    const loadedItems = typedData?.[dataPath] as TItem[] | undefined;
    const loadedToken = typedData?.nextPageToken;

    const [{ nextPageToken, items, isLoading, isLoadingNextPage }, setState] =
      useState<{
        isLoading: boolean;
        isLoadingNextPage: boolean;
        items?: TItem[];
        nextPageToken: string | null | undefined;
      }>({
        isLoading: loading,
        items: loadedItems ?? undefined,
        nextPageToken: typedData?.nextPageToken ?? null,
        isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
      });

    const handleNextPage = useCallback(async () => {
      if (nextPageToken && fetchMore) {
        await fetchMore({
          variables: {
            pageToken: nextPageToken,
          },
        });
      }
    }, [nextPageToken, fetchMore]);

    useEffect(() => {
      if (loading) {
        setState(current => ({
          ...current,
          isLoading: true,
          isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
        }));
      } else if (loadedItems) {
        setState(current => ({
          ...current,
          items: loadedItems,
          isLoading: false,
          isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
          nextPageToken: loadedToken,
        }));
      } else {
        setState(current => ({
          ...current,
          isLoading: false,
          isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
        }));
      }
    }, [loading, loadedItems, loadedToken, networkStatus]);

    const isEmpty = items?.length === 0 && !appError && !networkError;

    return {
      [dataPath]: items,
      hasNextPage: !!nextPageToken,
      error: appError ?? networkError,
      isLoading,
      nextPage: handleNextPage,
      isLoadingNextPage,
      isEmpty,
      refetch,
    } as StandardListHookResult<TItem, TPath>;
  };
}
